import React from 'react';
import { useMaintenanceData } from './useMaintenanceData';
import { SERVICES } from './maintenanceTypes';
import MaintenanceWindows from './MaintenanceWindows';
import MaintenanceDialogs from './MaintenanceDialogs';

export const MaintenanceManagementPanel: React.FC = () => {
  const model = useMaintenanceData();
  const {
    services, loading, isInMaintenance, notification, setNotification,
    setShowMaintenanceForm, setShowScheduleForm, fetchData,
    handleDisableMaintenance, setEditStatusService, setEditStatusValue,
    setEditStatusMessage, getStatusInfo,
  } = model;

  return (
    <div>
      {/* Notification */}
      {notification && (
        <div
          className={`admin-notice-row ${notification.type === 'success' ? 'admin-notice-success' : 'admin-notice-error'}`}
          style={{ padding: 0, marginBottom: 16 }}
          role={notification.type === 'error' ? 'alert' : 'status'}
        >
          <div className={`admin-notice ${notification.type === 'success' ? 'admin-notice-success' : 'admin-notice-error'}`} style={{ maxWidth: '100%', flex: 1 }}>
            <span className="admin-notice-message flex-1">{notification.message}</span>
            <button onClick={() => setNotification(null)} aria-label="Dismiss notification" className="admin-notice-dismiss">×</button>
          </div>
        </div>
      )}

      {/* Quick Actions Bar */}
      <div className="rp-bento-card mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold" style={{ color: 'var(--rp-text)' }}>System Status</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--rp-text-secondary)' }}>
              {isInMaintenance
                ? 'Maintenance mode is ACTIVE'
                : loading
                  ? 'Loading service status...'
                  : services.length === 0
                    ? 'No service status data available'
                    : SERVICES.some(svc => !services.some(s => s.service_name === svc))
                      ? 'Some service statuses unavailable'
                      : services.every(s => s.status === 'operational')
                        ? 'All services operational'
                        : 'Some services have issues'
              }
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            {isInMaintenance ? (
              <button onClick={handleDisableMaintenance}
                className="px-4 py-2 rounded-lg font-semibold text-sm text-white transition-all"
                style={{ backgroundColor: 'var(--admin-primary)' }}
              >
                End Maintenance
              </button>
            ) : (
              <button onClick={() => setShowMaintenanceForm(true)}
                className="px-4 py-2 rounded-lg font-semibold text-sm text-white transition-all"
                style={{ backgroundColor: 'var(--admin-warning-strong)' }}
              >
                Enable Maintenance
              </button>
            )}
            <button onClick={() => setShowScheduleForm(true)}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{ border: '1px solid var(--rp-border)', color: 'var(--rp-text)' }}
            >
              Schedule Window
            </button>
            <a href="/status" target="_blank" rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-all inline-flex items-center gap-1 min-h-[44px]"
              style={{ border: '1px solid var(--rp-border)', color: 'var(--rp-text)' }}
            >
              View Status Page ↗
            </a>
            <button onClick={fetchData} disabled={loading}
              className="px-4 py-2 rounded-lg text-sm transition-all disabled:opacity-50"
              style={{ border: '1px solid var(--rp-border)', color: 'var(--rp-text)' }}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Service Status Grid */}
      <div className="rp-bento-card mb-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--rp-text)' }}>Service Health</h3>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 rounded-full mx-auto mb-4" style={{ borderColor: 'var(--rp-border)', borderTopColor: 'var(--rp-text-secondary)' }} />
            <p style={{ color: 'var(--rp-text-secondary)' }}>Loading...</p>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-8">
            <p style={{ color: 'var(--rp-text-secondary)' }}>No service status data available</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {SERVICES.map(svc => {
              const svcData = services.find(s => s.service_name === svc);
              const missing = !svcData;
              const info = missing
                ? { label: 'Unavailable', color: 'var(--rp-text-tertiary)', defaultMessage: 'No service status data available' }
                : getStatusInfo(svcData.status);
              return (
                <button key={svc}
                  type="button"
                  aria-label={`Edit ${svc} status`}
                  className="p-4 rounded-lg text-left hover:ring-2 ring-white/20 transition-all"
                  style={{ backgroundColor: 'var(--rp-surface-secondary)', border: `1px solid color-mix(in srgb, ${info.color} 25%, transparent)` }}
                  onClick={() => {
                    setEditStatusService(svc);
                    setEditStatusValue(svcData?.status || 'operational');
                    setEditStatusMessage(svcData?.message || '');
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold capitalize" style={{ color: 'var(--rp-text)' }}>{svc}</span>
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: info.color }} aria-hidden="true" />
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `color-mix(in srgb, ${info.color} 12%, transparent)`, color: 'var(--rp-text)' }}>
                    {info.label}
                  </span>
                  <p className="text-xs mt-2 line-clamp-2" style={{ color: 'var(--rp-text-tertiary)' }}>
                    {missing ? info.defaultMessage : (svcData.message || info.defaultMessage)}
                  </p>
                </button>
              );
            })}
          </div>
        )}
        <p className="text-xs mt-3" style={{ color: 'var(--rp-text-tertiary)' }}>Click a service to change its status</p>
      </div>

      <MaintenanceWindows model={model} />
      <MaintenanceDialogs model={model} />
    </div>
  );
};
